<script setup lang="ts">
import type { createSortable } from "../../composables/Query/sortable";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useOptionalSortable } from "../../composables/Query/sortable";

interface Props {
  item?: {
    field: string
    translation: string
  }
  right?: boolean
  sortableHeading?: boolean
  /** The sort state to use instead of the provided one (VTable passes its own). */
  sortable?: ReturnType<typeof createSortable> | null
}

const props = withDefaults(defineProps<Props>(), {
  item: () => ({ field: "", translation: "" }),
  sortableHeading: false,
  right: false,
  sortable: null,
});

const { t } = useI18n();

// Tables without sortable headings do not provide a sortable, so the heading renders as plain text.
const injectedSortable = useOptionalSortable();
const sortableState = computed(() => props.sortable ?? injectedSortable);

const isClickable = computed(() => props.sortableHeading && !!sortableState.value?.isSortable(props.item.field));
const isSorted = computed(() => !!sortableState.value && props.item.field === sortableState.value.sort.value);
const sortDirection = computed(() => sortableState.value?.sortDirection.value ?? null);

/** Clicks cycle ascending, descending and no sort, as the headings always did. */
function setCurrentSort() {
  const sortable = sortableState.value;
  if (!sortable || !isClickable.value) {
    return;
  }

  const { sort, sortDirection } = sortable;

  if (sort.value !== props.item.field) {
    sort.value = props.item.field;
    sortDirection.value = "ASC";
    return;
  }

  if (sortDirection.value === "ASC") {
    sortDirection.value = "DESC";
    return;
  }

  sort.value = null;
  sortDirection.value = "ASC";
}

const hint = computed(() => t("base.table.sort_by", { column: props.item.translation }));
</script>

<template>
  <div
    class="flex h-full min-w-0"
    :class="{ 'w-full': right || isClickable }"
    @click="setCurrentSort"
  >
    <component
      :is="isClickable ? 'button' : 'div'"
      :type="isClickable ? 'button' : undefined"
      :title="isClickable ? hint : undefined"
      class="table-heading flex min-h-9 min-w-0 items-center gap-1.5 text-left text-xs font-medium text-gray-700 dark:text-gray-400"
      :class="{
        '-mx-2 w-[calc(100%+16px)] cursor-pointer justify-between rounded-md px-2 select-none transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:hover:bg-dark-700 dark:hover:text-gray-200': isClickable,
        'text-primary-900! dark:text-primary-400!': isSorted,
      }"
    >
      <span class="truncate">{{ item.translation }}</span>
      <span
        v-if="isClickable"
        class="flex shrink-0 flex-col gap-px"
        aria-hidden="true"
      >
        <svg width="8" height="5" viewBox="0 0 8 5" class="caret-up" :class="isSorted && sortDirection === 'ASC' ? 'text-primary-900 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'">
          <path d="M4 0l4 5H0z" fill="currentColor" />
        </svg>
        <svg width="8" height="5" viewBox="0 0 8 5" class="caret-down" :class="isSorted && sortDirection === 'DESC' ? 'text-primary-900 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'">
          <path d="M4 5 0 0h8z" fill="currentColor" />
        </svg>
      </span>
    </component>
  </div>
</template>
